import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Pencil, Save, X, Trash2, Plus, Info, Users, History, Paperclip,
  Building2, Globe, MapPin, ImagePlus, Star, Phone, Mail, MessageCircle,
  Copy, ExternalLink, FileText, Download, FileSignature,
} from "lucide-react";
import IconAction from "@/components/app/IconAction";
import Avatar, { fileToAvatarDataURL } from "@/components/app/Avatar";
import api, { fmtIDR, fmtDate } from "@/lib_app/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PO_STATUS } from "@/components/app/PoDetailModal";

const CAT_META = {
  pemerintah: {
    label: "Pemerintah",
    chip: "bg-[#FDE7E9] text-[#CE6968] border-[#FFB3BF]",
    heroBg: "linear-gradient(135deg, rgba(255,193,203,0.55), rgba(255,222,228,0.35))",
    heroBorder: "rgba(255,140,155,0.45)",
  },
  bumn: {
    label: "BUMN",
    chip: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]",
    heroBg: "linear-gradient(135deg, rgba(190,210,240,0.55), rgba(232,240,254,0.35))",
    heroBorder: "rgba(150,175,220,0.45)",
  },
  swasta: {
    label: "Swasta",
    chip: "bg-[#C9F0D6] text-[#4A6D53] border-[#8FD4A2]",
    heroBg: "linear-gradient(135deg, rgba(180,240,205,0.55), rgba(215,247,225,0.35))",
    heroBorder: "rgba(120,200,150,0.45)",
  },
  partner: {
    label: "Partner",
    chip: "bg-[#E6E2F4] text-[#4A3B77] border-[#C7BEE0]",
    heroBg: "linear-gradient(135deg, rgba(220,214,240,0.55), rgba(235,231,247,0.35))",
    heroBorder: "rgba(180,168,222,0.45)",
  },
};

function cleanPhone(raw) {
  let x = (raw || "").replace(/[^0-9+]/g, "");
  if (x.startsWith("0")) x = "62" + x.slice(1);
  else if (x.startsWith("+")) x = x.slice(1);
  else if (x.startsWith("8")) x = "62" + x;
  return x;
}

const EMPTY_NEW = {
  name: "",
  category: "swasta",
  is_customer: true,
  is_vendor: false,
  npwp: "",
  payment_terms: "",
  bank_accounts: [],
  address: "",
  website: "",
  notes: "",
  logo: "",
  pics: [{ id: null, name: "", position: "", phone: "", email: "", is_default: true }],
  sales_id: "",
  prospects: [],
};

export default function ClientDetailModal({ clientId, users = [], roleTab, onClose, onSaved }) {
  const { user: me } = useAuth();
  const isNew = clientId === "new";
  const isOpen = !!clientId;

  const [client, setClient] = useState(null);
  const [docs, setDocs] = useState({ attachments: [], contracts: [] });
  const [mode, setMode] = useState("read"); // read | edit
  const [tab, setTab] = useState("info");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [docBusy, setDocBusy] = useState(false);
  const fileRef = useRef(null);
  const docsFileRef = useRef(null);

  const canEdit = !!me; // any authenticated user can view; edits restricted at API
  const canDelDoc = me?.role === "owner" || me?.role === "sales_director";
  const canEditSales = me?.role === "owner" || me?.role === "sales_director";

  const userMap = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.user_id] = u; });
    return m;
  }, [users]);

  const loadDocs = async (id) => {
    if (!id || id === "new") { setDocs({ attachments: [], contracts: [] }); return; }
    try {
      const r = await api.get(`/clients/${id}/attachments`);
      setDocs(r.data || { attachments: [], contracts: [] });
    } catch { setDocs({ attachments: [], contracts: [] }); }
  };

  const loadClient = async (id) => {
    if (!id || id === "new") return;
    try {
      const r = await api.get(`/clients/${id}`);
      setClient(r.data);
    } catch { toast.error("Gagal memuat klien"); }
  };

  useEffect(() => {
    if (!isOpen) return;
    setTab("info");
    if (isNew) {
      const init = {
        ...EMPTY_NEW,
        sales_id: me?.user_id || "",
        sales_name: me?.name || "",
        sales_nickname: me?.nickname || me?.name || "",
        sales_picture: me?.picture || "",
      };
      setClient(init);
      setForm(init);
      setDocs({ attachments: [], contracts: [] });
      setMode("edit");
    } else {
      setMode("read");
      loadClient(clientId);
      loadDocs(clientId);
    }
    // eslint-disable-next-line
  }, [clientId]);

  // Sync form when entering edit mode
  useEffect(() => {
    if (mode === "edit" && client && !isNew) {
      setForm({
        name: client.name || "",
        category: client.category || "swasta",
        is_customer: client.is_customer !== false,
        is_vendor: !!client.is_vendor,
        npwp: client.npwp || "",
        payment_terms: client.payment_terms || "",
        bank_accounts: (client.bank_accounts || []).map((b) => ({ ...b })),
        address: client.address || "",
        website: client.website || "",
        notes: client.notes || "",
        logo: client.logo || "",
        pics: (client.pics || []).map((p) => ({ ...p })),
        sales_id: client.sales_id || "",
      });
    }
  }, [mode, client, isNew]);

  const catMeta = CAT_META[client?.category] || CAT_META.swasta;
  const isVendorView = roleTab === "vendor";
  const totalProspects = client?.prospect_count ?? (client?.prospects || []).length;
  const wonCount = (client?.prospects || []).filter((p) => p.status === "won").length;
  const totalDealValue = client?.total_deal_value ?? (client?.prospects || []).filter((p) => p.status === "won").reduce((s, p) => s + (p.realization || 0), 0);
  const activePicCount = (client?.pics || []).filter((p) => p.name || p.phone || p.email).length;
  const totalPOs = client?.po_count ?? (client?.purchase_orders || []).length;
  const poWithProjectCount = client?.po_with_project_count ?? 0;
  const poTotalValue = client?.po_total_value ?? 0;
  const statTotal = isVendorView ? totalPOs : totalProspects;
  const statWon = isVendorView ? poWithProjectCount : wonCount;
  const statValue = isVendorView ? poTotalValue : totalDealValue;
  const showProspectsSection = client?.is_customer !== false;
  const showPOSection = !!client?.is_vendor;
  const riwayatCount = (showProspectsSection ? totalProspects : 0) + (showPOSection ? totalPOs : 0);

  const save = async () => {
    if (!form?.name?.trim()) { toast.error("Nama klien wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        category: form.category,
        address: form.address || "",
        website: form.website || "",
        notes: form.notes || "",
        logo: form.logo || "",
        pics: form.pics || [],
        sales_id: form.sales_id,
        is_customer: !!form.is_customer,
        is_vendor: !!form.is_vendor,
        npwp: form.npwp || "",
        payment_terms: form.payment_terms || "",
        bank_accounts: (form.bank_accounts || []).filter((b) => (b.bank_name || "").trim() || (b.account_no || "").trim()),
      };
      if (isNew) {
        const r = await api.post(`/clients`, payload);
        toast.success("Klien baru ditambahkan");
        onSaved?.();
        onClose();
        return;
      }
      await api.patch(`/clients/${clientId}`, payload);
      toast.success("Klien diperbarui");
      await loadClient(clientId);
      setMode("read");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal simpan");
    } finally { setSaving(false); }
  };

  const cancelEdit = () => {
    if (isNew) { onClose(); return; }
    setMode("read");
  };

  const pickLogo = () => fileRef.current?.click();
  const onFileLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("File harus gambar");
    try {
      setLogoBusy(true);
      const dataUrl = await fileToAvatarDataURL(f);
      setForm((d) => ({ ...d, logo: dataUrl }));
      toast.success("Logo dipilih — klik Simpan untuk menerapkan");
    } catch { toast.error("Gagal memproses logo"); }
    finally { setLogoBusy(false); }
  };

  const uploadDocs = async (fileList) => {
    if (!fileList || fileList.length === 0 || isNew) return;
    const fd = new FormData();
    Array.from(fileList).forEach((f) => fd.append("files", f));
    setDocBusy(true);
    try {
      await api.post(`/clients/${clientId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadDocs(clientId);
      toast.success(`${fileList.length} dokumen diupload`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal upload"); }
    finally { setDocBusy(false); }
  };

  const removeDoc = async (att) => {
    if (!canDelDoc) return;
    if (!confirm(`Hapus dokumen "${att.filename}"?`)) return;
    try {
      if (att.source === "prospect" && att.context?.prospect_id) {
        await api.delete(`/prospects/${att.context.prospect_id}/attachments/${att.id}`);
      } else {
        await api.delete(`/clients/${clientId}/attachments/${att.id}`);
      }
      await loadDocs(clientId);
      toast.success("Dokumen dihapus");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal hapus"); }
  };

  const downloadDoc = (att) => {
    const token = localStorage.getItem("sp_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/attachments/${att.id}/download?auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank");
  };

  const copyText = async (text, label) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); toast.success(`${label} disalin`); }
    catch { toast.error("Gagal salin"); }
  };

  if (!isOpen) return null;
  if (!client) {
    return (
      <Dialog open onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-2xl rounded-2xl">
          <DialogTitle className="sr-only">Memuat klien</DialogTitle>
          <div className="p-6 text-sm text-[#A0A3A8]">Memuat...</div>
        </DialogContent>
      </Dialog>
    );
  }

  const tabs = [
    { key: "info", label: "Informasi", icon: Info },
    { key: "pic", label: "PIC", icon: Users, count: activePicCount },
    { key: "riwayat", label: "Riwayat Prospek", icon: History, count: riwayatCount },
    { key: "lampiran", label: "Lampiran", icon: Paperclip, count: (docs.attachments?.length || 0) + (docs.contracts?.length || 0) },
  ];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-5xl max-h-[92vh] overflow-y-auto rounded-3xl border-0 p-0"
        data-testid="client-detail-modal"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(232,240,254,0.92) 100%)",
          boxShadow: "0 32px 80px -20px rgba(59,74,99,0.30)",
        }}
      >
        <DialogTitle className="sr-only">Detail Klien {client.name}</DialogTitle>
        <DialogDescription className="sr-only">Detail lengkap klien</DialogDescription>

        <div className="p-6 space-y-5 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#6B8FE2] font-bold">
            {isNew ? "Klien Baru" : mode === "edit" ? "Detail Klien · Mode Edit" : "Detail Klien"}
          </p>

          {/* HERO */}
          <div className="rounded-2xl p-5 backdrop-blur-xl" style={{ background: catMeta.heroBg, border: `1px solid ${catMeta.heroBorder}`, boxShadow: "0 10px 30px -12px rgba(20,22,31,0.12), inset 0 1px 0 rgba(255,255,255,0.55)" }}>
            <div className="flex items-start gap-4 mb-4">
              <ClientLogo logo={mode === "edit" ? form?.logo : client.logo} name={mode === "edit" ? form?.name : client.name} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${catMeta.chip}`}>
                    {catMeta.label}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-[#090808] leading-tight truncate">{isNew ? (form?.name || "Klien Baru") : client.name}</h2>
                {!isNew && client.address && (
                  <p className="text-xs text-[#626061] mt-1 flex items-center gap-1.5 truncate">
                    <MapPin className="w-3 h-3 shrink-0" /> {client.address}
                  </p>
                )}
              </div>
              {!isNew && client.website && (
                <a
                  href={client.website.startsWith("http") ? client.website : `https://${client.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="client-hero-website"
                  className="inline-flex items-center h-7 px-3 rounded-full text-[11px] font-bold uppercase tracking-widest bg-white/70 text-[#3B4A63] border border-white/80 hover:bg-white shrink-0 shadow-sm transition"
                >
                  <Globe className="w-3 h-3 mr-1.5 text-[#A0A3A8]" />
                  Website
                </a>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
              <div className="flex items-center gap-2">
                <Avatar name={client.sales_name} picture={client.sales_picture} size="sm" className="!w-7 !h-7 !text-[11px]" />
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Sales PIC</div>
                  <div className="text-sm font-semibold text-[#090808]">{client.sales_nickname || client.sales_name || "Belum ditugaskan"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* STAT STRIP — only for existing */}
          {!isNew && (
            <div className="grid grid-cols-4 rounded-2xl bg-white/70 border border-white/80 backdrop-blur-xl overflow-hidden" style={{ boxShadow: "0 6px 18px rgba(59,74,99,0.08)" }} data-testid="client-stat-strip">
              <div className="p-4 border-r border-[#DCE0E7]/60 min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold truncate">Total Prospek</div>
                <div className="text-lg font-bold text-[#090808] font-num tabular-nums mt-1 truncate">{statTotal}</div>
              </div>
              <div className="p-4 border-r border-[#DCE0E7]/60 min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold truncate">Deal Menang</div>
                <div className={`text-lg font-bold font-num tabular-nums mt-1 truncate ${statWon > 0 ? "text-[#57D28A]" : "text-[#A0A3A8]"}`}>{statWon}</div>
              </div>
              <div className="p-4 border-r border-[#DCE0E7]/60 min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold truncate">Total Nilai</div>
                <div className={`text-lg font-bold font-num tabular-nums mt-1 truncate ${statValue > 0 ? "text-[#57D28A]" : "text-[#A0A3A8]"}`}>{statValue > 0 ? fmtIDR(statValue) : "—"}</div>
              </div>
              <div className="p-4 min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold truncate">PIC Aktif</div>
                <div className="text-lg font-bold text-[#090808] font-num tabular-nums mt-1 truncate">{activePicCount}</div>
              </div>
            </div>
          )}

          {/* TAB BAR */}
          <div className="flex items-center justify-between border-b border-[#DCE0E7] gap-3">
            <div className="flex items-center overflow-x-auto flex-1">
              {tabs.map((t) => {
                const Ico = t.icon;
                const active = tab === t.key;
                const hideForNew = isNew && (t.key === "riwayat" || t.key === "lampiran");
                if (hideForNew) return null;
                return (
                  <button
                    key={t.key} onClick={() => setTab(t.key)} data-testid={`client-tab-${t.key}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-3 text-sm transition-colors whitespace-nowrap border-b-2 ${
                      active ? (mode === "edit" ? "text-[#3B4A63] border-[#3B4A63] font-bold" : "text-[#090808] border-[#090808] font-bold") : "text-[#A0A3A8] border-transparent hover:text-[#626061]"
                    }`}
                  >
                    <Ico className="w-3.5 h-3.5" />
                    {t.label}
                    {typeof t.count === "number" && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${active ? "bg-[#090808] text-white" : "bg-[#F4F5F7] text-[#626061]"}`}>{t.count}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 pb-2">
              {mode === "read" && canEdit && !isNew && (
                <IconAction icon={Pencil} label="Edit" variant="info" size="sm" onClick={() => setMode("edit")} testid="client-edit-toggle" />
              )}
              {mode === "edit" && (
                <>
                  <IconAction icon={X} label="Batal" size="sm" onClick={cancelEdit} disabled={saving} testid="client-edit-cancel" />
                  <IconAction icon={Save} label={saving ? "Menyimpan…" : "Simpan"} variant="primary" size="sm" onClick={save} disabled={saving} testid="client-save-btn" />
                </>
              )}
            </div>
          </div>

          {/* TAB CONTENT */}
          {tab === "info" && (
            mode === "read" ? (
              <ReadInfo client={client} />
            ) : (
              <EditInfo
                form={form}
                setForm={setForm}
                users={users}
                canEditSales={canEditSales}
                onPickLogo={pickLogo}
                onRemoveLogo={() => setForm((d) => ({ ...d, logo: "" }))}
                logoBusy={logoBusy}
                fileRef={fileRef}
                onFileLogo={onFileLogo}
              />
            )
          )}

          {tab === "pic" && (
            mode === "read"
              ? <ReadPICs pics={client.pics || []} clientName={client.name || ""} onCopy={copyText} />
              : <EditPICs form={form} setForm={setForm} />
          )}

          {tab === "riwayat" && !isNew && (
            <TabRiwayat
              prospects={client.prospects || []}
              purchaseOrders={client.purchase_orders || []}
              showProspects={showProspectsSection}
              showPO={showPOSection}
            />
          )}

          {tab === "lampiran" && !isNew && (
            <TabLampiran
              docs={docs}
              docBusy={docBusy}
              canDelDoc={canDelDoc}
              inputRef={docsFileRef}
              userMap={userMap}
              onUpload={uploadDocs}
              onDownload={downloadDoc}
              onRemove={removeDoc}
            />
          )}

          {/* FOOTER */}
          <div className="pt-4 border-t border-[#E5EEFA]/60 flex items-center justify-end gap-2">
            <IconAction icon={X} label="Tutup" onClick={onClose} testid="client-modal-close" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ========== Helpers & Sub-components ========== */

function ClientLogo({ logo, name, size = "md" }) {
  const px = size === "sm" ? "w-8 h-8 text-[10px]" : size === "lg" ? "w-16 h-16 text-lg" : "w-11 h-11 text-xs";
  const initials = (name || "").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  if (logo) {
    return <img src={logo} alt={name} className={`${px} rounded-xl object-cover shrink-0 border border-[#DCE0E7] bg-white`} />;
  }
  return (
    <div className={`${px} rounded-xl bg-white/80 text-[#3B4A63] font-bold flex items-center justify-center shrink-0 border border-[#DCE0E7]`}>
      {initials || <Building2 className="w-5 h-5" />}
    </div>
  );
}

function InfoRow({ label, value, valueClass = "" }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-[#F4F5F7] last:border-0 gap-4">
      <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold shrink-0 pt-0.5">{label}</span>
      <span className={`text-sm text-[#090808] font-semibold text-right min-w-0 flex-1 break-words [overflow-wrap:anywhere] whitespace-pre-wrap ${valueClass}`}>{value}</span>
    </div>
  );
}

function ReadInfo({ client }) {
  const cat = CAT_META[client.category] || CAT_META.swasta;
  return (
    <div className="space-y-0 rounded-2xl bg-white/50 border border-[#E5EEFA]/60 px-5 pt-2 pb-4" data-testid="client-info-read">
      <InfoRow label="Nama Mitra" value={client.name || "-"} />
      <InfoRow label="Peran" value={[client.is_customer !== false ? "Klien" : null, client.is_vendor ? "Vendor" : null].filter(Boolean).join(" + ") || "-"} />
      {client.is_vendor && <InfoRow label="NPWP Vendor" value={client.npwp || "-"} />}
      {client.is_vendor && <InfoRow label="Termin Pembayaran" value={client.payment_terms || "-"} />}
      {client.is_vendor && (
        <InfoRow label="Rekening Vendor" value={(client.bank_accounts || []).length === 0 ? "-" : (
          <div className="space-y-1">
            {client.bank_accounts.map((b) => (
              <div key={b.id} className="text-sm"><b>{b.bank_name}</b> · <span className="font-num">{b.account_no}</span> <span className="text-[#A0A3A8]">a.n. {b.holder}</span></div>
            ))}
          </div>
        )} />
      )}
      <InfoRow label="Kategori" value={<span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${cat.chip}`}>{cat.label}</span>} />
      <InfoRow label="Website" value={client.website ? <a href={client.website.startsWith("http") ? client.website : `https://${client.website}`} target="_blank" rel="noopener noreferrer" className="text-[#3B4A63] hover:underline inline-flex items-center gap-1">{client.website.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" /></a> : <span className="text-[#A0A3A8]">—</span>} />
      <InfoRow label="Sales PIC" value={client.sales_nickname || client.sales_name || "-"} />
      <InfoRow label="Alamat" value={client.address || <span className="text-[#A0A3A8]">—</span>} />
      <div className="pt-4">
        <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-2">Catatan Umum</div>
        {client.notes ? (
          <div className="rounded-xl bg-white/70 border border-[#DCE0E7]/70 p-3 text-sm text-[#3B4A63] whitespace-pre-wrap">{client.notes}</div>
        ) : (
          <div className="rounded-xl bg-[#F9FAFC] border border-[#DCE0E7]/70 p-3 text-sm text-[#A0A3A8] italic">Belum ada catatan.</div>
        )}
      </div>
    </div>
  );
}

function EditInfo({ form, setForm, users, canEditSales, onPickLogo, onRemoveLogo, logoBusy, fileRef, onFileLogo }) {
  if (!form) return null;
  const set = (k, v) => setForm((d) => ({ ...d, [k]: v }));
  return (
    <div className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-5 space-y-4">
      {/* Logo block */}
      <div className="flex items-center gap-4 p-4 bg-white/70 rounded-xl border border-[#DCE0E7]">
        <ClientLogo logo={form.logo} name={form.name} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-[#090808]">Logo Klien</div>
          <p className="text-xs text-[#A0A3A8] mt-0.5">PNG/JPG persegi, otomatis di-resize.</p>
          <div className="flex items-center gap-2 mt-2">
            <Button
              type="button"
              size="sm"
              onClick={onPickLogo}
              disabled={logoBusy}
              data-testid="client-logo-upload-btn"
              className="bg-[#090808] hover:bg-black gap-1.5 h-8"
            >
              <ImagePlus className="w-3.5 h-3.5" /> {form.logo ? "Ganti Logo" : "Upload Logo"}
            </Button>
            {form.logo && (
              <button type="button" onClick={onRemoveLogo} data-testid="client-logo-remove-btn" className="inline-flex items-center gap-1 text-[11px] text-[#A0A3A8] hover:text-[#FF6B7D] h-8 px-2 rounded-lg">
                <X className="w-3 h-3" /> Hapus
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileLogo} data-testid="client-logo-file-input" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-[#DCE0E7] bg-[#FAFBFD] p-4 mb-3">
        <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Peran Mitra</Label>
        <div className="flex items-center gap-2 mt-2">
          {[["is_customer", "Klien (Customer)"], ["is_vendor", "Vendor (Supplier)"]].map(([k, l]) => (
            <button
              key={k} type="button" onClick={() => set(k, !form[k])} data-testid={`client-role-${k}`}
              className={`h-9 px-4 rounded-full text-xs font-bold border transition-colors ${form[k] ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"}`}
            >{l}</button>
          ))}
        </div>
        <p className="text-[11px] text-[#A0A3A8] mt-2">Satu perusahaan boleh berperan sebagai klien sekaligus vendor.</p>
      </div>

      {form.is_vendor && (
        <div className="rounded-2xl border border-[#DCE0E7] bg-white p-4 mb-3 space-y-3" data-testid="client-vendor-section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">NPWP Vendor</Label>
              <Input value={form.npwp || ""} onChange={(e) => set("npwp", e.target.value)} placeholder="00.000.000.0-000.000" className="rounded-xl bg-white h-11 mt-1 font-num" data-testid="client-edit-npwp" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Termin Pembayaran</Label>
              <Input value={form.payment_terms || ""} onChange={(e) => set("payment_terms", e.target.value)} placeholder="mis. NET 30" className="rounded-xl bg-white h-11 mt-1" data-testid="client-edit-payment-terms" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Rekening Vendor</Label>
            <div className="space-y-2 mt-1.5">
              {(form.bank_accounts || []).map((b, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`client-bank-row-${i}`}>
                  <Input value={b.bank_name || ""} onChange={(e) => set("bank_accounts", form.bank_accounts.map((x, j) => (j === i ? { ...x, bank_name: e.target.value } : x)))} placeholder="Nama bank" className="rounded-xl bg-white h-10" data-testid={`client-bank-name-${i}`} />
                  <Input value={b.account_no || ""} onChange={(e) => set("bank_accounts", form.bank_accounts.map((x, j) => (j === i ? { ...x, account_no: e.target.value } : x)))} placeholder="No. rekening" className="rounded-xl bg-white h-10 font-num" data-testid={`client-bank-account-${i}`} />
                  <Input value={b.holder || ""} onChange={(e) => set("bank_accounts", form.bank_accounts.map((x, j) => (j === i ? { ...x, holder: e.target.value } : x)))} placeholder="Atas nama" className="rounded-xl bg-white h-10" data-testid={`client-bank-holder-${i}`} />
                  <button type="button" onClick={() => set("bank_accounts", form.bank_accounts.filter((_, j) => j !== i))} data-testid={`client-bank-remove-${i}`} className="w-9 h-9 rounded-full hover:bg-[#FDE7E9] text-[#CE6968] inline-flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button
                type="button" onClick={() => set("bank_accounts", [...(form.bank_accounts || []), { bank_name: "", account_no: "", holder: "" }])}
                data-testid="client-bank-add"
                className="h-9 px-4 rounded-full bg-[#F4F5F7] hover:bg-[#E5EEFA] text-xs font-bold text-[#3B4A63] inline-flex items-center gap-1.5"
              ><Plus className="w-3.5 h-3.5" /> Tambah Rekening</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Nama Mitra</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl bg-white h-11 mt-1" data-testid="client-edit-name" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Kategori</Label>
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger className="rounded-xl bg-white h-11 mt-1" data-testid="client-edit-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(CAT_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Website</Label>
          <div className="relative mt-1">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A3A8]" />
            <Input value={form.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://..." className="rounded-xl bg-white h-11 pl-9" data-testid="client-edit-website" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Sales PIC</Label>
          {canEditSales ? (
            <Select value={form.sales_id || ""} onValueChange={(v) => set("sales_id", v)}>
              <SelectTrigger className="rounded-xl bg-white h-11 mt-1" data-testid="client-edit-sales"><SelectValue placeholder="Pilih sales" /></SelectTrigger>
              <SelectContent>
                {(users || []).filter((u) => u.is_active && u.role !== "finance").map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    <div className="flex items-center gap-2">
                      <Avatar name={u.name} picture={u.picture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0" />
                      <span className="text-sm font-semibold">{u.nickname || u.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="h-11 mt-1 flex items-center gap-2 text-sm px-3 rounded-xl bg-white border border-[#DCE0E7]">
              <Avatar name={form.sales_name} picture={form.sales_picture} size="xs" /> {form.sales_nickname}
            </div>
          )}
        </div>
        <div className="md:col-span-3">
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Alamat</Label>
          <div className="relative mt-1">
            <MapPin className="absolute left-3 top-3 w-4 h-4 text-[#A0A3A8]" />
            <Textarea rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="rounded-xl bg-white pl-9" data-testid="client-edit-address" />
          </div>
        </div>
        <div className="md:col-span-3">
          <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Catatan Umum</Label>
          <Textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Info kunci tentang klien, gaya komunikasi, dsb." className="rounded-xl bg-white mt-1" data-testid="client-edit-notes" />
        </div>
      </div>
    </div>
  );
}

/* ---------- PIC Tabs ---------- */

function ReadPICs({ pics, clientName, onCopy }) {
  const rows = (pics || []).filter((p) => p.name || p.phone || p.email);
  if (rows.length === 0) {
    return <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]" data-testid="client-pic-empty">Belum ada PIC untuk klien ini. Klik Edit untuk menambahkan.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="client-pic-read">
      {rows.map((p) => {
        const waLink = p.phone ? `https://wa.me/${cleanPhone(p.phone)}?text=${encodeURIComponent(`Halo${p.name ? " " + p.name : ""}, saya dari tim IPTec${clientName ? ` — terkait ${clientName}` : ""}.`)}` : "";
        return (
          <div key={p.id || `${p.name}-${p.phone}`} className={`rounded-2xl p-4 transition ${p.is_default ? "bg-gradient-to-br from-[#FFF8E0] to-[#FFFDF3] border-2 border-[#F5D66B]" : "bg-white border border-[#DCE0E7]"}`} data-testid={`client-pic-read-${p.id || p.name}`}>
            {p.is_default && (
              <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#FFDB52] text-[#7A5B00] mb-2">
                <Star className="w-3 h-3" fill="currentColor" /> PIC Utama
              </span>
            )}
            <div className="text-base font-bold text-[#090808]">{p.name || "-"}</div>
            <div className="text-xs text-[#626061] mb-3">{p.position || "-"}</div>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center gap-2 text-[#090808] font-num tabular-nums">
                <Phone className="w-3.5 h-3.5 text-[#3B4A63] shrink-0" />
                <span className="truncate">{p.phone || <span className="text-[#A0A3A8]">Belum diisi</span>}</span>
                {p.phone && (
                  <div className="ml-auto flex items-center gap-1.5 shrink-0">
                    <a href={waLink} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#25D366] hover:bg-[#1EB855] text-white flex items-center justify-center" title="WhatsApp"><MessageCircle className="w-3.5 h-3.5" /></a>
                    <button onClick={() => onCopy(p.phone, "No HP")} className="w-8 h-8 rounded-full bg-white border border-[#DCE0E7] text-[#3B4A63] hover:bg-[#E5EEFA] flex items-center justify-center" title="Salin"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-[#090808]">
                <Mail className="w-3.5 h-3.5 text-[#3B4A63] shrink-0" />
                <span className="truncate">{p.email || <span className="text-[#A0A3A8]">Belum diisi</span>}</span>
                {p.email && (
                  <div className="ml-auto flex items-center gap-1.5 shrink-0">
                    <a href={`mailto:${p.email}`} className="w-8 h-8 rounded-full bg-[#3B4A63] hover:bg-[#2C3B54] text-white flex items-center justify-center" title="Email"><Mail className="w-3.5 h-3.5" /></a>
                    <button onClick={() => onCopy(p.email, "Email")} className="w-8 h-8 rounded-full bg-white border border-[#DCE0E7] text-[#3B4A63] hover:bg-[#E5EEFA] flex items-center justify-center" title="Salin"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EditPICs({ form, setForm }) {
  if (!form) return null;
  const setPic = (i, k, v) => setForm((d) => {
    const pics = [...(d.pics || [])];
    pics[i] = { ...pics[i], [k]: v };
    return { ...d, pics };
  });
  const addPic = () => setForm((d) => ({
    ...d, pics: [...(d.pics || []), { id: null, name: "", position: "", phone: "", email: "", is_default: (d.pics || []).length === 0 }],
  }));
  const rmPic = (i) => setForm((d) => ({ ...d, pics: (d.pics || []).filter((_, idx) => idx !== i) }));
  const setDefaultPic = (i) => setForm((d) => ({ ...d, pics: (d.pics || []).map((p, idx) => ({ ...p, is_default: idx === i })) }));

  return (
    <div className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-[#090808]">PIC ({(form.pics || []).length})</div>
        <Button size="sm" variant="outline" onClick={addPic} data-testid="client-add-pic" className="gap-1 h-8 rounded-full"><Plus className="w-3 h-3" /> Tambah PIC</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(form.pics || []).map((p, i) => (
          <PicEditCard
            key={p.id || i}
            pic={p}
            index={i}
            clientName={form.name || ""}
            onChange={(k, v) => setPic(i, k, v)}
            onSetDefault={() => setDefaultPic(i)}
            onRemove={() => rmPic(i)}
          />
        ))}
        {(form.pics || []).length === 0 && (
          <div className="col-span-full text-xs text-[#A0A3A8] bg-white/70 border border-[#DCE0E7] rounded-xl p-4 text-center">
            Belum ada PIC. Klik <b>+ Tambah PIC</b> untuk menambahkan kontak.
          </div>
        )}
      </div>
    </div>
  );
}

function PicEditCard({ pic: p, index, clientName, onChange, onSetDefault, onRemove }) {
  const waLink = (phone) => {
    const num = cleanPhone(phone);
    if (!num) return "";
    const msg = encodeURIComponent(`Halo${p.name ? " " + p.name : ""}, saya dari tim IPTec${clientName ? ` — ingin follow up terkait ${clientName}` : ""}.`);
    return `https://wa.me/${num}?text=${msg}`;
  };
  const doCopy = async (text, label) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); toast.success(`${label} disalin`); }
    catch { toast.error("Gagal salin"); }
  };

  return (
    <div className={`rounded-2xl border p-3.5 relative ${p.is_default ? "bg-gradient-to-br from-[#FFF8E0] to-[#FFFDF3] border-[#F5D66B]" : "bg-white border-[#DCE0E7]"}`} data-testid={`client-pic-card-${index}`}>
      <div className="flex items-center justify-between mb-2.5">
        <button
          onClick={onSetDefault}
          title={p.is_default ? "PIC utama" : "Set sebagai utama"}
          data-testid={`client-pic-star-${index}`}
          className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-colors ${
            p.is_default ? "bg-[#FFDB52] text-[#7A5B00]" : "bg-white border border-[#DCE0E7] text-[#A0A3A8] hover:text-[#7A5B00] hover:border-[#F5D66B]"
          }`}
        >
          <Star className="w-3 h-3" fill={p.is_default ? "currentColor" : "none"} />
          {p.is_default ? "Utama" : "Set utama"}
        </button>
        <button
          onClick={onRemove}
          data-testid={`client-pic-remove-${index}`}
          className="w-7 h-7 rounded-full flex items-center justify-center text-[#A0A3A8] hover:text-[#FF6B7D] hover:bg-[#F8ECE5] transition-colors"
          title="Hapus PIC"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-5 gap-2 mb-2.5">
        <div className="col-span-3">
          <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold">Nama</Label>
          <Input placeholder="Nama lengkap" value={p.name || ""} onChange={(e) => onChange("name", e.target.value)} className="bg-white h-9 mt-0.5 rounded-xl" data-testid={`client-pic-name-${index}`} />
        </div>
        <div className="col-span-2">
          <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold">Jabatan</Label>
          <Input placeholder="Direktur, PIC, dsb." value={p.position || ""} onChange={(e) => onChange("position", e.target.value)} className="bg-white h-9 mt-0.5 rounded-xl" data-testid={`client-pic-position-${index}`} />
        </div>
      </div>

      <div className="mb-2.5">
        <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold flex items-center gap-1"><Phone className="w-3 h-3" /> No HP</Label>
        <div className="flex items-center gap-1.5 mt-0.5">
          <Input placeholder="0812xxxx" value={p.phone || ""} onChange={(e) => onChange("phone", e.target.value)} className="bg-white h-9 rounded-xl flex-1 font-num tabular-nums" data-testid={`client-pic-phone-${index}`} />
          <a
            href={p.phone ? waLink(p.phone) : undefined}
            target="_blank" rel="noopener noreferrer"
            onClick={(e) => { if (!p.phone) e.preventDefault(); }}
            data-testid={`client-pic-wa-${index}`}
            title={p.phone ? `Chat WhatsApp` : "Isi nomor dulu"}
            className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${p.phone ? "bg-[#25D366] hover:bg-[#1EB855] text-white" : "bg-[#F4F5F7] text-[#A0A3A8] cursor-not-allowed"}`}
          >
            <MessageCircle className="w-4 h-4" />
          </a>
          <button type="button" onClick={() => doCopy(p.phone, "No HP")} disabled={!p.phone} data-testid={`client-pic-phone-copy-${index}`} title="Salin" className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${p.phone ? "bg-white border border-[#DCE0E7] text-[#3B4A63] hover:bg-[#E5EEFA]" : "bg-[#F4F5F7] text-[#A0A3A8] cursor-not-allowed"}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
        <div className="flex items-center gap-1.5 mt-0.5">
          <Input placeholder="nama@perusahaan.com" type="email" value={p.email || ""} onChange={(e) => onChange("email", e.target.value)} className="bg-white h-9 rounded-xl flex-1" data-testid={`client-pic-email-${index}`} />
          {p.email && (
            <a href={`mailto:${p.email}`} data-testid={`client-pic-mailto-${index}`} title={`Email ke ${p.email}`} className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-[#3B4A63] hover:bg-[#2C3B54] text-white transition-colors">
              <Mail className="w-4 h-4" />
            </a>
          )}
          <button type="button" onClick={() => doCopy(p.email, "Email")} disabled={!p.email} data-testid={`client-pic-email-copy-${index}`} title="Salin" className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${p.email ? "bg-white border border-[#DCE0E7] text-[#3B4A63] hover:bg-[#E5EEFA]" : "bg-[#F4F5F7] text-[#A0A3A8] cursor-not-allowed"}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Riwayat Prospek / PO Tab ---------- */

function SectionLabel({ icon: Icon, label, count }) {
  return (
    <div className="flex items-center gap-1.5 mb-2.5 mt-1">
      <Icon className="w-3.5 h-3.5 text-[#A0A3A8]" />
      <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold">{label}</span>
      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-[#F4F5F7] text-[#626061]">{count}</span>
    </div>
  );
}

function ProspectRow({ p }) {
  const isWonWithProject = p.status === "won" && p.project_id;
  const href = isWonWithProject ? `/projects?open=${p.project_id}` : `/prospects?open=${p.id}`;
  const linkLabel = isWonWithProject ? "PROYEK" : p.status === "won" ? "DEAL" : p.status === "lost" ? "LOST" : "PROSPEK";
  const linkTint = isWonWithProject ? "bg-[#E5EEFA] text-[#3B4A63]" : p.status === "won" ? "bg-[#E4F9EC] text-[#3EB876]" : p.status === "lost" ? "bg-[#F4F5F7] text-[#626061]" : "bg-[#FFDB52] text-[#7A5B00]";
  return (
    <a
      key={p.id}
      href={href}
      data-testid={`client-prospect-link-${p.id}`}
      title={isWonWithProject ? "Buka proyek terkait" : "Buka prospek"}
      className="flex items-center justify-between gap-3 rounded-2xl bg-white border border-[#DCE0E7]/60 hover:border-[#B0C7EA] hover:shadow-md transition p-4"
    >
      <div className="min-w-0 flex-1 flex items-center gap-3">
        <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest shrink-0 ${linkTint}`}>
          {linkLabel}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[#090808] truncate">{p.service}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {(p.owner_nickname || p.owner_name) && (
              <Avatar name={p.owner_name} picture={p.owner_picture} size="sm" className="!w-4 !h-4 !text-[8px] !border-0 shrink-0" />
            )}
            <span className="text-[11px] text-[#A0A3A8] truncate">
              {p.owner_nickname || p.owner_name || "-"}
              {p.pipeline_month ? ` · ${p.pipeline_month}` : ""}
            </span>
          </div>
        </div>
      </div>
      <div className="text-right shrink-0">
        {p.status === "won" ? (
          <div className="text-sm font-bold text-[#57D28A] font-num tabular-nums">{fmtIDR(p.realization || 0)}</div>
        ) : (
          <div className="text-sm font-semibold text-[#090808] font-num tabular-nums">{fmtIDR(p.prospect_value || 0)}</div>
        )}
        <div className="text-[10px] text-[#A0A3A8] uppercase tracking-widest font-bold">{p.status === "won" ? "Realisasi" : "Nilai"}</div>
      </div>
      <ExternalLink className="w-3.5 h-3.5 text-[#A0A3A8] shrink-0" />
    </a>
  );
}

function PoRow({ po }) {
  const st = PO_STATUS[po.status] || PO_STATUS.draft;
  const hasProject = !!po.project_id;
  const href = `/purchase-orders?open=${po.id}`;
  return (
    <a
      key={po.id}
      href={href}
      data-testid={`client-po-link-${po.id}`}
      title="Buka Purchase Order"
      className="flex items-center justify-between gap-3 rounded-2xl bg-white border border-[#DCE0E7]/60 hover:border-[#B0C7EA] hover:shadow-md transition p-4"
    >
      <div className="min-w-0 flex-1 flex items-center gap-3">
        <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest shrink-0 border ${st.chip}`}>
          {st.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[#090808] truncate font-num">{po.number}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`text-[11px] truncate ${hasProject ? "text-[#3B4A63] font-semibold" : "text-[#A0A3A8]"}`}>
              {hasProject ? po.project_title : "Belum terhubung proyek"}
              {po.date ? ` · ${fmtDate(po.date)}` : ""}
            </span>
          </div>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-bold text-[#090808] font-num tabular-nums">{fmtIDR(po.grand_total || 0)}</div>
        <div className="text-[10px] text-[#A0A3A8] uppercase tracking-widest font-bold">Nilai PO</div>
      </div>
      <ExternalLink className="w-3.5 h-3.5 text-[#A0A3A8] shrink-0" />
    </a>
  );
}

function TabRiwayat({ prospects, purchaseOrders, showProspects, showPO }) {
  const hasProspects = prospects && prospects.length > 0;
  const hasPO = purchaseOrders && purchaseOrders.length > 0;
  const showBoth = showProspects && showPO;

  if (!showProspects && !showPO) {
    return <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]" data-testid="client-riwayat-empty">Belum ada riwayat untuk mitra ini.</div>;
  }

  return (
    <div className="space-y-5" data-testid="client-riwayat-list">
      {showProspects && (
        <div>
          {showBoth && <SectionLabel icon={History} label="Riwayat Prospek" count={prospects.length} />}
          {hasProspects ? (
            <div className="space-y-2">{prospects.map((p) => <ProspectRow key={p.id} p={p} />)}</div>
          ) : (
            <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-6 text-center text-sm text-[#A0A3A8]" data-testid="client-riwayat-prospek-empty">Belum ada prospek untuk klien ini.</div>
          )}
        </div>
      )}
      {showPO && (
        <div>
          {showBoth && <SectionLabel icon={FileText} label="Riwayat Purchase Order" count={purchaseOrders.length} />}
          {hasPO ? (
            <div className="space-y-2">{purchaseOrders.map((po) => <PoRow key={po.id} po={po} />)}</div>
          ) : (
            <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-6 text-center text-sm text-[#A0A3A8]" data-testid="client-riwayat-po-empty">Belum ada Purchase Order untuk vendor ini.</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Lampiran Tab ---------- */

function TabLampiran({ docs, docBusy, canDelDoc, inputRef, userMap, onUpload, onDownload, onRemove }) {
  const iconForFile = (name) => {
    const ext = (name || "").split(".").pop()?.toLowerCase();
    if (["pdf"].includes(ext)) return { label: "PDF", bg: "bg-[#FDE7E9]", fg: "text-[#CE6968]" };
    if (["xls", "xlsx", "csv"].includes(ext)) return { label: "XLS", bg: "bg-[#E4F9EC]", fg: "text-[#3EB876]" };
    if (["doc", "docx"].includes(ext)) return { label: "DOC", bg: "bg-[#DDEBFB]", fg: "text-[#2F4B70]" };
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return { label: "IMG", bg: "bg-[#E6E2F4]", fg: "text-[#4A3B77]" };
    return { label: "FILE", bg: "bg-[#F4F5F7]", fg: "text-[#626061]" };
  };
  const attachments = docs?.attachments || [];
  const contracts = docs?.contracts || [];

  return (
    <div className="space-y-4" data-testid="client-lampiran">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
        className="hidden"
        onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }}
        data-testid="client-doc-file-input"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={docBusy}
        data-testid="client-doc-upload-btn"
        className="w-full rounded-2xl border-2 border-dashed border-[#B0C7EA] bg-[#F5F9FF]/50 hover:bg-[#EAF2FD]/70 transition py-6 flex flex-col items-center gap-1"
      >
        <Paperclip className="w-5 h-5 text-[#3B4A63]" />
        <div className="text-sm font-semibold text-[#3B4A63]">{docBusy ? "Mengupload…" : "Klik untuk upload dokumen"}</div>
        <div className="text-[10px] text-[#A0A3A8]">PO · Kontrak · Invoice · Company Profile · max 15MB per file</div>
      </button>

      {contracts.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-2 flex items-center gap-1.5">
            <FileSignature className="w-3 h-3" /> Nomor Kontrak ({contracts.length})
          </div>
          <div className="space-y-1.5">
            {contracts.map((c) => (
              <div key={c.prospect_id} className="flex items-center justify-between gap-2 rounded-xl bg-white border border-[#DCE0E7]/60 px-3 py-2 text-xs" data-testid={`client-contract-${c.prospect_id}`}>
                <div className="min-w-0 truncate">
                  <span className="font-bold text-[#090808] font-num tabular-nums">{c.contract_no}</span>
                  <span className="text-[#A0A3A8] ml-1.5">· {c.service}</span>
                </div>
                <div className="text-[#57D28A] font-semibold shrink-0 ml-2 tabular-nums font-num">{fmtIDR(c.realization)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-2">Dokumen ({attachments.length})</div>
        {attachments.length === 0 ? (
          <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]">Belum ada dokumen untuk klien ini.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {attachments.map((a) => {
              const ic = iconForFile(a.filename);
              const uploader = a.uploaded_by ? (userMap || {})[a.uploaded_by] : null;
              const uploaderName = (uploader?.nickname || uploader?.name) || a.uploaded_by_name || "";
              return (
                <div key={a.id} onClick={() => onDownload(a)} className="rounded-2xl bg-white border border-[#DCE0E7]/60 p-4 flex items-center gap-3 hover:shadow-md transition cursor-pointer" data-testid={`client-doc-${a.id}`}>
                  <div className={`w-11 h-11 rounded-xl ${ic.bg} flex items-center justify-center ${ic.fg} font-bold text-[10px] shrink-0`}>{ic.label}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#090808] truncate">{a.filename}</div>
                    <div className="text-[11px] text-[#A0A3A8] mt-0.5 flex items-center gap-1.5 min-w-0">
                      <span className="truncate">
                        {a.source === "prospect" ? `Prospek · ${a.context?.service || ""}` : "Klien-level"}
                      </span>
                      {uploaderName && (
                        <>
                          <span className="shrink-0">·</span>
                          <Avatar name={uploader?.name || a.uploaded_by_name} picture={uploader?.picture} size="sm" className="!w-4 !h-4 !text-[8px] !border-0 shrink-0" />
                          <span className="truncate">{uploaderName}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); onDownload(a); }} className="w-8 h-8 rounded-full hover:bg-[#F4F5F7] text-[#3B4A63] flex items-center justify-center shrink-0" title="Buka"><Download className="w-3.5 h-3.5" /></button>
                  {canDelDoc && (
                    <button onClick={(e) => { e.stopPropagation(); onRemove(a); }} className="w-8 h-8 rounded-full hover:bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0" title="Hapus" data-testid={`client-doc-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
